import { useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Check, X, Eye, Calendar } from "@/components/icons";

const STATUS_INFO = {
  pendiente: { l: "Pendiente", dot: "bg-amber-500" },
  aceptado: { l: "Aceptado", dot: "bg-emerald-500" },
  rechazado: { l: "Rechazado", dot: "bg-red-500" },
  completado: { l: "Completado", dot: "bg-[#0071E3]" },
};

/**
 * ReferralChatCard — tarjeta Apple minimalista para una burbuja de referido.
 * Diseño: fondo blanco, borde fino, chip de urgencia + estado con dot,
 * motivo truncado, acciones compactas. Si el receptor rechaza, se expande
 * un input para la razón corta; esa razón se guarda en `status_note` y se
 * muestra a ambos lados al refrescar.
 */
export default function ReferralChatCard({
  message,
  isReceiver,
  onStatusChange,
  onCreateAppointment,
}) {
  const [busy, setBusy] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectNote, setRejectNote] = useState("");

  const meta = message.meta || {};
  const status = meta.status || "pendiente";
  const urgency = meta.urgency || "rutina";
  const refId = message.referral_id;
  const statusInfo = STATUS_INFO[status] || STATUS_INFO.pendiente;

  const updateStatus = async (newStatus, note = "") => {
    if (busy) return;
    setBusy(true);
    try {
      await api.patch(`/referrals/${refId}/status`, {
        status: newStatus,
        note: note || undefined,
      });
      toast.success(
        newStatus === "aceptado"
          ? "Referido aceptado"
          : newStatus === "rechazado"
            ? "Referido rechazado"
            : "Actualizado"
      );
      setShowRejectForm(false);
      setRejectNote("");
      onStatusChange?.(newStatus);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Error al actualizar";
      toast.error(typeof msg === "string" ? msg : "Error");
    } finally {
      setBusy(false);
    }
  };

  const confirmReject = (e) => {
    e?.preventDefault?.();
    if (!rejectNote.trim()) {
      toast.error("Indica una razón breve");
      return;
    }
    updateStatus("rechazado", rejectNote.trim());
  };

  const openPrint = () => {
    window.open(`/referidos/${refId}/imprimir`, "_blank", "noopener");
  };

  const createAppt = () => {
    const pid = meta.imported_patient_id;
    if (!pid) {
      toast.error("Primero acepta el referido para crear la cita");
      return;
    }
    onCreateAppointment?.(pid);
  };

  return (
    <div
      data-testid={`referral-card-${refId}`}
      className="w-full max-w-[300px] bg-white border border-black/[0.08] rounded-2xl overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_14px_-4px_rgba(0,0,0,0.06)]"
    >
      {/* Top row: urgencia + estado — ambos como chips sutiles */}
      <div className="px-3.5 pt-3 pb-1.5 flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-black/55">
          <span
            className={`w-1.5 h-1.5 rounded-full ${
              urgency === "urgente" ? "bg-red-500" : "bg-black/40"
            }`}
          />
          Referido {urgency === "urgente" && "· Urgente"}
        </span>
        <span className="inline-flex items-center gap-1 text-[9.5px] font-semibold uppercase tracking-wider text-black/50">
          <span className={`w-1.5 h-1.5 rounded-full ${statusInfo.dot}`} />
          {statusInfo.l}
        </span>
      </div>

      {/* Body */}
      <div className="px-3.5 pb-2.5 space-y-2">
        <div>
          <div className="text-[9.5px] uppercase tracking-wider font-semibold text-black/40">
            Paciente
          </div>
          <div className="text-[13.5px] font-bold text-black truncate leading-snug">
            {meta.patient_name || "Paciente"}
          </div>
        </div>
        <div>
          <div className="text-[9.5px] uppercase tracking-wider font-semibold text-black/40">
            Motivo
          </div>
          <div className="text-[12px] text-black/75 leading-snug line-clamp-3">
            {meta.reason || "Sin motivo"}
          </div>
        </div>
        {status === "rechazado" && meta.status_note && (
          <div className="bg-red-50 border border-red-100 rounded-xl px-2.5 py-2">
            <div className="text-[9.5px] uppercase tracking-wider font-semibold text-red-600/80">
              Razón del rechazo
            </div>
            <div className="text-[12px] text-red-700/90 leading-snug mt-0.5">
              {meta.status_note}
            </div>
          </div>
        )}
      </div>

      {/* Form inline de rechazo */}
      {showRejectForm && (
        <form
          onSubmit={confirmReject}
          className="px-3.5 pb-2.5 space-y-2 border-t border-black/[0.04] pt-2.5 bg-[#FAFAFC]"
        >
          <input
            autoFocus
            type="text"
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            placeholder="Razón breve del rechazo"
            maxLength={200}
            data-testid={`referral-card-reject-note-${refId}`}
            className="w-full h-9 px-3 rounded-xl bg-white border border-black/[0.08] text-[12px] text-black placeholder-black/40 focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition"
          />
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => {
                setShowRejectForm(false);
                setRejectNote("");
              }}
              className="flex-1 h-8 rounded-xl bg-white border border-black/[0.08] text-[11px] font-semibold text-black/60 hover:bg-black/[0.03] transition"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={busy || !rejectNote.trim()}
              data-testid={`referral-card-reject-confirm-${refId}`}
              className="flex-1 h-8 rounded-xl bg-red-500 text-white text-[11px] font-bold hover:bg-red-600 disabled:opacity-50 transition"
            >
              Rechazar
            </button>
          </div>
        </form>
      )}

      {/* Acciones principales */}
      {!showRejectForm && (
        <div className="px-2.5 pb-2.5 flex items-center gap-1.5">
          <button
            type="button"
            onClick={openPrint}
            data-testid={`referral-card-view-${refId}`}
            className="h-8 px-3 rounded-xl text-[11.5px] font-semibold text-black/70 hover:bg-black/[0.04] transition inline-flex items-center gap-1"
          >
            <Eye className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
            Ver
          </button>
          {isReceiver && status === "pendiente" && (
            <>
              <button
                type="button"
                onClick={() => setShowRejectForm(true)}
                disabled={busy}
                data-testid={`referral-card-reject-${refId}`}
                className="h-8 px-3 rounded-xl text-[11.5px] font-semibold text-red-600 hover:bg-red-50 transition inline-flex items-center gap-1 disabled:opacity-50"
              >
                <X className="w-3.5 h-3.5" strokeWidth={2} weight="regular" />
                Rechazar
              </button>
              <button
                type="button"
                onClick={() => updateStatus("aceptado")}
                disabled={busy}
                data-testid={`referral-card-accept-${refId}`}
                className="ml-auto h-8 px-3.5 rounded-xl bg-black text-white text-[11.5px] font-bold hover:bg-black/85 transition inline-flex items-center gap-1 disabled:opacity-50"
              >
                <Check className="w-3.5 h-3.5" strokeWidth={2.5} weight="bold" />
                Aceptar
              </button>
            </>
          )}
          {isReceiver && status === "aceptado" && (
            <button
              type="button"
              onClick={createAppt}
              data-testid={`referral-card-create-appt-${refId}`}
              className="ml-auto h-8 px-3.5 rounded-xl bg-[#0071E3] text-white text-[11.5px] font-bold hover:bg-[#0077ED] transition inline-flex items-center gap-1"
            >
              <Calendar className="w-3.5 h-3.5" strokeWidth={2} weight="regular" />
              Crear cita
            </button>
          )}
        </div>
      )}
    </div>
  );
}
